import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';

import { partitionSlotsByStep } from '@/components/agreements/boardSlots';
import { AgreementSealingProvider, useAgreementSealing } from '@/components/agreements/useAgreementSealing';
import type { AgreementRenderPayload } from '@/lib/agreements/api';
import AgreementsArea from '@/components/contracts/setup/areas/AgreementsArea';
import DepositArea from '@/components/contracts/setup/areas/DepositArea';
import DriverArea from '@/components/contracts/setup/areas/DriverArea';
import FinancialsArea from '@/components/contracts/setup/areas/FinancialsArea';
import InsuranceArea from '@/components/contracts/setup/areas/InsuranceArea';
import VehicleArea from '@/components/contracts/setup/areas/VehicleArea';
import { focusJumpTarget, useDossierChrome } from '@/components/contracts/setup/dossier/useDossierChrome';
import StatsLedger, { LedgerSection } from '@/components/contracts/setup/ledger/StatsLedger';
import ActivateGate, { ActivationConfirm } from '@/components/contracts/setup/manifest/ActivateGate';
import ActivationReceipt from '@/components/contracts/setup/manifest/ActivationReceipt';
import Manifest from '@/components/contracts/setup/manifest/Manifest';
import NarratorLine from '@/components/contracts/setup/manifest/NarratorLine';
import { journeyLabel } from '@/components/contracts/setup/manifest/manifestView';
import { useActivation } from '@/components/contracts/setup/manifest/useActivation';
import type { ManifestRow, ManifestRowId } from '@/components/contracts/setup/setupState';
import { SETUP_AREAS, areaPath, resolveArea, type SetupArea } from '@/components/contracts/setup/steps';
import { useContractSetupState } from '@/components/contracts/setup/useContractSetupState';

/**
 * Contract setup, as THE DOSSIER — one page, three regions, six area routes.
 *
 * The shape is the whole argument. The LEFT PANE is the contract's name, the NARRATOR's one
 * sentence of what is happening, and the MANIFEST of every requirement in the journey with who
 * is holding it up and for how long — one scroller, end to end, with the activation control
 * pinned at its foot. The RIGHT PANE is the CANVAS: one area at a time at an 880px measure,
 * scrolling independently, top to bottom of the viewport. Both are rendered from
 * `useContractSetupState` — one selector, three views — which is what makes it structurally
 * impossible for the page to claim a contract is ready while the server refuses it. The screen
 * this replaces derived completion in four places and the four disagreed.
 *
 * THE THIRD REGION IS THE LEDGER, and it shows at EVERY width (owner's ruling). Read-only
 * statistics — where this contract stands, how long it has been open, what is at stake and who is
 * being waited on — drawn from the same `state` and `snapshot` the other two are, so it is a third
 * VIEW rather than a third derivation.
 *
 * IT HAS TWO HOMES BECAUSE IT CANNOT HAVE THREE COLUMNS EVERYWHERE. At 1440 and above it is the
 * PANE below: a 222px aside in the right margin, the grid's third track. Below 1440 the measured
 * ink troughs fall under the page's 52px floor, so the same four modules render instead as the
 * LAST SECTION OF THE CANVAS (`LedgerSection`, inside `main` above), two fixed-measure columns
 * under the area's foot. One shared body, one order, and `index.css` picks the form — see
 * `StatsLedger.tsx`.
 *
 * NOTHING SPANS THE TWO. A band across the top is a band both panes pay for at every scroll
 * offset, and this page had 144px of one; the identity block is the left pane's own head, and
 * the canvas owns the full height of the content area.
 *
 * THE AREA LIVES IN THE PATH, not in a query parameter, and that is what makes each area a real
 * place: a merge-field pre-flight links to `/setup/financials`, a half-done setup is
 * bookmarkable, and the back button walks the areas rather than the history of one screen.
 * Every `?step=` link ever minted still works — it redirects here, replacing itself so the back
 * button does not bounce between the two spellings.
 *
 * A ROUTE-LESS ENTRY RESOLVES ITSELF, ONCE. `/setup` with no area is the cold open, and it lands
 * the admin on the area with the longest outstanding EXTERNAL lead time — the DVLA registration
 * before the typing, because that is the clock that has not started. The guard mechanics matter
 * as much as the rule: it waits for every read to answer (three of them are `staleTime: 0` and
 * would otherwise resolve from a half-empty snapshot), it fires exactly once through a ref latch,
 * and it is an unconditional no-op whenever an area is already in the URL — so a background
 * refetch can never move an admin off the area they are typing into.
 *
 * "REVIEW" IS NOT AN AREA. Activation is the manifest's pinned footer, visible from day one with
 * the count of unmet gates, so the final gate has been watched all week rather than discovered
 * at the end of a wizard.
 *
 * There is no new permission. The route sits under `/dashboard/contracts/...`, and
 * `canAccessRoute` matches on the first path segment.
 */
export default function ContractSetup() {
    const { contractId: contractIdParam, area: areaParam } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const contractId = parseInt(contractIdParam || '0', 10);
    const routeArea = resolveArea(areaParam);
    const legacyStep = searchParams.get('step');

    const { rootRef } = useDossierChrome<HTMLDivElement>();
    const [activeRowId, setActiveRowId] = useState<ManifestRowId | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    /**
     * `missing[]` for a document a row has already opened.
     *
     * IT IS READ, NEVER FETCHED. `['agreement-render', documentId]` is a lazy per-row entry and IS
     * the shown-hash record every freezing RPC compares against — pulling it up to page level would
     * render documents nobody opened and move the hash a countersignature attests to. So the
     * snapshot takes whatever the canvas has already put in the cache, and the witness lock falls
     * back to the hire purchase agreement's own legacy `signed_at` when nothing has.
     */
    const renderMissing = useCallback(
        (documentId: number) =>
            queryClient.getQueryData<AgreementRenderPayload>(['agreement-render', documentId])
                ?.missing ?? null,
        [queryClient],
    );

    const activation = useActivation({ contractId });
    const { state, snapshot, stateReady, landingArea, setup, agreements } = useContractSetupState({
        contractId,
        refusals: activation.refusals,
        renderMissing,
    });

    /**
     * ONE SEALING LATCH FOR THE WHOLE PAGE, keyed off the DOCUMENTS rather than off any area's
     * slots — a COMPLETED document that no mounted canvas claims used to be sealed by nobody at
     * all. Provided from here so every slot list and every deposit block on the page joins it
     * instead of racing it into `AGREEMENT_FINALIZE_IN_PROGRESS`, which the loser then reports as
     * a failed seal needing a human.
     */
    const sealing = useAgreementSealing({
        documents: agreements.documents,
        onRefresh: agreements.refreshBoard,
    });

    const slotsByArea = useMemo(() => partitionSlotsByStep(agreements.slots), [agreements.slots]);

    const goToArea = useCallback(
        (area: SetupArea) => navigate(areaPath(contractId, area)),
        [navigate, contractId],
    );

    /**
     * Every legacy `?step=` link, redirected to its area route.
     *
     * `review` resolves to NOTHING on purpose: the review step retired into the manifest footer,
     * so its old links land on the bare route and the landing rule takes it from there. Other
     * query parameters are carried across — a link is allowed to carry more than a step.
     */
    useEffect(() => {
        if (!legacyStep) return;
        const params = new URLSearchParams(searchParams);
        params.delete('step');
        const query = params.toString();
        navigate(`${areaPath(contractId, resolveArea(legacyStep))}${query ? `?${query}` : ''}`, {
            replace: true,
        });
    }, [legacyStep, searchParams, contractId, navigate]);

    const landed = useRef(false);
    useEffect(() => {
        if (routeArea) return;
        if (legacyStep) return;
        if (!stateReady) return;
        if (landed.current) return;
        landed.current = true;
        navigate(areaPath(contractId, landingArea), { replace: true });
    }, [routeArea, legacyStep, stateReady, landingArea, contractId, navigate]);

    const activeArea = routeArea ?? landingArea;
    const areaIndex = SETUP_AREAS.findIndex((candidate) => candidate.key === activeArea);
    const area = SETUP_AREAS[areaIndex >= 0 ? areaIndex : 0];
    const previousArea = areaIndex > 0 ? SETUP_AREAS[areaIndex - 1] : null;
    const nextArea = areaIndex >= 0 && areaIndex < SETUP_AREAS.length - 1 ? SETUP_AREAS[areaIndex + 1] : null;

    /**
     * Selecting a row IS the navigation: route to the area that owns it, then put the caret in
     * the field it names. Both halves are allowed to miss — a canvas that has not been rebuilt
     * yet simply lands the admin at the top of the right area.
     */
    const selectRow = useCallback(
        (row: ManifestRow) => {
            setActiveRowId(row.id);
            setSheetOpen(false);
            if (row.jump.area !== activeArea) goToArea(row.jump.area);
            focusJumpTarget(rootRef.current, row.jump.block, row.jump.focus);
        },
        [activeArea, goToArea, rootRef],
    );

    /**
     * A selected row belongs to the area it lives in. Walking to another area (by the area footer
     * or by any link) drops the selection, so the manifest's marker never sits on a row two groups
     * away from where the canvas actually is.
     */
    useEffect(() => {
        if (!activeRowId) return;
        const selected = state.rows.find((candidate) => candidate.id === activeRowId);
        if (selected && selected.area !== activeArea) setActiveRowId(null);
    }, [activeArea, activeRowId, state.rows]);

    const areaContent = () => {
        if (!routeArea && !stateReady) {
            return (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            );
        }

        switch (area.key) {
            case 'deposit':
                return (
                    <DepositArea
                        contractId={contractId}
                        deposits={setup.depositPayments}
                        loading={!stateReady && !setup.depositPayments}
                        driverName={snapshot.driver.fullName}
                        driverPhone={snapshot.driver.phone}
                        onGoToArea={goToArea}
                        onSuccess={setup.refetchAll}
                    />
                );
            case 'vehicle':
                return (
                    <VehicleArea
                        contractId={contractId}
                        vehicleId={setup.vehicleId}
                        vehicle={setup.contract?.vehicles ?? null}
                        plate={snapshot.vehicle.registration}
                        mileage={setup.contract?.vehicle_mileage}
                        contractStatus={setup.contractStatus}
                        agreements={agreements}
                        loading={setup.isLoadingPrep}
                        getItemData={setup.getItemData}
                        onSuccess={setup.refetchAll}
                    />
                );
            case 'driver':
                return contractId ? (
                    <DriverArea contractId={contractId} onSuccess={setup.refetchAll} />
                ) : null;
            case 'insurance':
                return !setup.vehicleId ? (
                    <p className="sd-hint">
                        No vehicle is assigned yet. A policy is bound to a car, so cover cannot be
                        recorded until this contract has one.
                    </p>
                ) : (
                    <InsuranceArea
                        contractId={contractId}
                        vehicleId={setup.vehicleId}
                        // WHAT A CERTIFICATE HAS TO NAME — both out of the envelope this page
                        // already holds: the plate through the snapshot, exactly as `VehicleArea`
                        // takes it, and the chassis off the same embed `VehicleArea` is handed
                        // whole. The check costs no read of its own.
                        vehiclePlate={snapshot.vehicle.registration}
                        vehicleVin={setup.contract?.vehicles?.vin ?? null}
                        slots={slotsByArea.insurance}
                        agreements={agreements}
                        onGoToArea={goToArea}
                        onSuccess={setup.refetchAll}
                    />
                );
            case 'financials':
                return (
                    <FinancialsArea
                        contractId={contractId}
                        contract={setup.contract ?? null}
                        instalment={setup.instalment ?? null}
                        deposits={setup.depositPayments}
                        insuranceLoan={setup.insurance?.loans?.[0] ?? null}
                        contractStatus={setup.contractStatus}
                        agreements={agreements}
                        onGoToArea={goToArea}
                        onSuccess={setup.refetchAll}
                    />
                );
            case 'agreements':
                return (
                    <AgreementsArea
                        contractId={contractId}
                        slots={slotsByArea.agreements}
                        depositSlots={slotsByArea.deposit}
                        insuranceSlots={slotsByArea.insurance}
                        agreements={agreements}
                        driver={snapshot.driver}
                        witness={snapshot.witness}
                        vehicle={snapshot.vehicle}
                        onGoToArea={goToArea}
                    />
                );
            default:
                return null;
        }
    };

    const driverName = setup.driverProfile
        ? `${setup.driverProfile.first_name ?? ''} ${setup.driverProfile.last_name ?? ''}`.trim()
        : '';

    const gateFooterNode = <ActivateGate state={state} activation={activation} />;

    /**
     * ONE SET OF INPUTS FOR BOTH LEDGER HOMES. The aside and the canvas section are two renderings
     * of one column, so they are handed one object rather than two spellings of four props — the
     * one way this page could make them disagree is by feeding them differently.
     */
    const ledgerProps = {
        state,
        snapshot,
        tokens: agreements.tokens,
        contractCreatedAt: setup.contract?.created_at ?? null,
    };

    return (
        <AgreementSealingProvider value={sealing}>
        <div className="setup-dossier -m-4 lg:-m-6" ref={rootRef}>
            {/*
              * TWO PANES, EACH SCROLLING ITSELF, AND NO BAND ACROSS THE TOP OF EITHER.
              *
              * The identity block is the LEFT COLUMN'S HEAD, inside the same scroller as the
              * groups: the contract's name scrolls away with the journey it names, and the only
              * thing pinned in that column is the activation control at its foot. Nothing spans
              * both columns, so the canvas begins at the top of the content area and uses the
              * full height of it — the band that used to sit above both panes was 144px of the
              * viewport that the work itself could never occupy.
              *
              * The title block is the pricing screen's, verbatim: `text-4xl font-semibold
              * tracking-tight` over a `text-muted-foreground` line
              * (`PricingPage.tsx:673-674`, `RenewalPricingPage.tsx:160-164`), and it starts on
              * the same leading edge as every numeral, dot and control in the column beneath it
              * — the page gutter those screens get from `px-4 sm:px-6 lg:px-8` over the layout's
              * own `p-4 lg:p-6`, which `--sd-gutter` reproduces for a surface that cancels that
              * padding to run full-bleed.
              *
              * THE SUBTITLE IS THE NARRATOR. It used to be a band of its own, which meant the
              * masthead was two stacked bands saying two things at one width — and the second of
              * them was the only line on the page that changes. As the subtitle it costs no
              * chrome at all and sits where every other screen in the dashboard puts the line
              * that says what you are looking at. `aria-live` travels with it (`NarratorLine`),
              * so a signature landing still announces itself.
              *
              * NO CHROME ABOVE OR BESIDE THE TITLE. The breadcrumb, the stage badge and the plate
              * badge are gone: three annotations wrapped around a heading, none of which was the
              * reason anybody opened this screen. What they carried has not gone missing — the
              * stage is the narrator's whole subject, the plate is on the vehicle area's own
              * canvas, and the way back is the app's `Contracts` nav.
              */}
            <Manifest
                state={state}
                activeArea={activeArea}
                activeRowId={activeRowId}
                onSelectRow={selectRow}
                footer={gateFooterNode}
                head={
                    <header className="sd-hdr">
                        <div className="sd-hdr-top">
                            {/*
                              * IT WRAPS, IT DOES NOT ELIDE. The identity line used to run the
                              * width of the page and could be clipped with an ellipsis at no
                              * cost; in a 340px pane `Emmanuel Kwabena Asante-Mensah` clips to
                              * `Emmanuel Kwa…`, and a person's name is the one string on this
                              * screen that must arrive whole. It is also the only thing above
                              * the fold that identifies the contract.
                              */}
                            <h1 className="min-w-0 text-4xl font-semibold tracking-tight">
                                {driverName || `Contract #${contractId}`}
                            </h1>
                            {/* Below 1024px the journey is a sheet, and this is the way into it. */}
                            <Button
                                variant="outline"
                                size="sm"
                                className="ml-auto h-8 flex-none lg:hidden"
                                aria-expanded={sheetOpen}
                                onClick={() => setSheetOpen(true)}
                            >
                                {journeyLabel(state.progress)}
                            </Button>
                        </div>

                        <NarratorLine
                            state={state}
                            onJump={selectRow}
                            onActivate={activation.openConfirm}
                            onOpenReceipt={activation.openReceipt}
                        />
                    </header>
                }
            />

            <main className="sd-canvas-wrap">
                <div className="sd-canvas">
                    <h2 className="sd-area-h">{area.label}</h2>
                    <p className="sd-area-q">{area.question}</p>

                    {areaContent()}

                    <nav className="sd-area-foot" aria-label="Areas">
                        {previousArea ? (
                            <button
                                type="button"
                                className="sd-af-btn"
                                onClick={() => goToArea(previousArea.key)}
                            >
                                <span>Previous</span>
                                {previousArea.label}
                            </button>
                        ) : (
                            <span />
                        )}
                        {nextArea && (
                            <button
                                type="button"
                                className="sd-af-btn sd-af-btn-right"
                                onClick={() => goToArea(nextArea.key)}
                            >
                                <span>Next</span>
                                {nextArea.label}
                            </button>
                        )}
                    </nav>

                    {/*
                      * THE LEDGER'S NARROW HOME — the last section of the canvas, below 1440px.
                      *
                      * It is INSIDE `.sd-canvas` on purpose: that is what makes it inherit the
                      * canvas's own scrollport between 1024 and 1439, and the page's scroll below
                      * 1024 where the canvas IS the page. It also keeps it out of the grid, so it
                      * can never become a fourth column of `.setup-dossier`.
                      *
                      * AFTER THE AREA FOOT, not before it: Previous/Next is the end of the WORK,
                      * and the figures are what is true once the work is put down. The separation
                      * is `--sd-area-gap` of nothing at all — the page's biggest trough and not a
                      * rule, because this surface has no rules on it.
                      */}
                    <LedgerSection {...ledgerProps} />
                </div>
            </main>

            {/*
              * THE LEDGER'S WIDE HOME — the third IN-FLOW CHILD of the grid, at 1440px and above.
              *
              * ⚠ FUTURE AUTHORS: `.setup-dossier` is a grid whose track list is spelled out in
              * `index.css`, so every in-flow child here is a COLUMN. There are exactly three, and
              * the THREE elements below this one are not among them: the `Drawer` is portalled to
              * `document.body` by vaul, `ActivationConfirm` is a Radix `AlertDialog` and portalled
              * the same way, and `ActivationReceipt` is `fixed inset-0` — and an absolutely
              * positioned child of a grid is not a grid item. Anything added here that is neither
              * portalled nor taken out of flow becomes a fourth column, or (worse, because it is
              * silent) an implicit second ROW under all three panes.
              *
              * The aside is `display: none` below 1440px, and a `display: none` child generates no
              * grid item at all — which is what lets the same markup serve a two-track grid
              * everywhere else without a single conditional here. The narrow form of these same
              * modules is `LedgerSection`, inside the canvas above.
              */}
            <StatsLedger {...ledgerProps} />

            {/* Below 1024px the manifest is this sheet, behind the persistent Journey button. */}
            <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
                <DrawerContent className="sd-tokens h-[78vh] max-h-[78vh]">
                    <DrawerTitle className="sr-only">Journey manifest</DrawerTitle>
                    <Manifest
                        state={state}
                        activeArea={activeArea}
                        activeRowId={activeRowId}
                        onSelectRow={selectRow}
                        footer={gateFooterNode}
                        variant="sheet"
                    />
                </DrawerContent>
            </Drawer>

            <ActivationConfirm state={state} snapshot={snapshot} activation={activation} />

            {activation.receiptOpen && (
                <ActivationReceipt
                    snapshot={snapshot}
                    activatedAt={activation.activatedAt ?? ''}
                    onClose={activation.closeReceipt}
                    onOpenContract={() => navigate(`/dashboard/contracts/${contractId}`)}
                />
            )}
        </div>
        </AgreementSealingProvider>
    );
}
